import { useEffect, useState, useCallback } from "react";
import { Sun, Moon, MonitorPlay } from "@phosphor-icons/react";

/**
 * Three-state theme toggle: light → dark → system → light …
 * - Persists to localStorage.
 * - System mode listens to `prefers-color-scheme` and follows it live.
 * - Applies `.dark` class to <html> (Tailwind's darkMode: "class" convention).
 */

const STORAGE_KEY = "symposia:theme"; // "light" | "dark" | "system"

function applyTheme(mode) {
  const root = document.documentElement;
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const effective = mode === "system" ? (prefersDark ? "dark" : "light") : mode;
  root.classList.toggle("dark", effective === "dark");
  root.setAttribute("data-theme", effective);
}

export function initTheme() {
  const saved = localStorage.getItem(STORAGE_KEY) || "system";
  applyTheme(saved);
  // React to OS-level changes when in system mode.
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    const cur = localStorage.getItem(STORAGE_KEY) || "system";
    if (cur === "system") applyTheme("system");
  });
}

export default function ThemeToggle() {
  const [mode, setMode] = useState(
    typeof window !== "undefined" ? (localStorage.getItem(STORAGE_KEY) || "system") : "system"
  );

  const cycle = useCallback(() => {
    const next = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
    setMode(next);
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
  }, [mode]);

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  const label = mode === "light" ? "Light" : mode === "dark" ? "Dark" : "System";
  const Icon = mode === "light" ? Sun : mode === "dark" ? Moon : MonitorPlay;

  return (
    <button
      type="button"
      onClick={cycle}
      data-testid="theme-toggle"
      aria-label={`Theme: ${label}. Click to cycle.`}
      title={`Theme: ${label}`}
      className="inline-flex items-center justify-center w-9 h-9 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-zinc-700 dark:text-zinc-300"
    >
      <Icon size={16} weight="regular" />
    </button>
  );
}
